export const RULER_SIZE = 20;
export const IDEAL_MAJOR_TICK_SPACING_PX = 100;

const TICK_MULTIPLIERS = [1, 2, 5] as const;

export interface RulerTick {
  value: number;
  kind: "minor" | "medium" | "major";
  lengthPx: number;
}

export function rulerMajorStep(zoom: number): number {
  const idealUnits = IDEAL_MAJOR_TICK_SPACING_PX / Math.max(0.0001, zoom);
  const exponent = Math.floor(Math.log10(Math.max(idealUnits, 1e-9)));
  const base = 10 ** exponent;
  let best = TICK_MULTIPLIERS[0]! * base;
  let bestDistance = Math.abs(best - idealUnits);

  for (const multiplier of TICK_MULTIPLIERS) {
    const candidate = multiplier * base;
    const distance = Math.abs(candidate - idealUnits);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }

  if (best < idealUnits) {
    const nextExponent = 10 ** (exponent + 1);
    for (const multiplier of TICK_MULTIPLIERS) {
      const candidate = multiplier * nextExponent;
      const distance = Math.abs(candidate - idealUnits);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
  }

  return best;
}

export function rulerTicksForRange(
  zoom: number,
  start: number,
  end: number,
): RulerTick[] {
  const majorStep = rulerMajorStep(zoom);
  const minorStep = majorStep / 10;
  const min = Math.min(start, end);
  const max = Math.max(start, end);
  const first = Math.floor(min / minorStep) * minorStep;
  const ticks: RulerTick[] = [];

  for (let value = first; value <= max + minorStep * 0.5; value += minorStep) {
    const majorIndex = Math.round(value / majorStep);
    const majorValue = majorIndex * majorStep;
    const isMajor = Math.abs(value - majorValue) < minorStep * 0.05;
    const mediumIndex = Math.round(value / (majorStep / 2));
    const mediumValue = mediumIndex * (majorStep / 2);
    const isMedium = !isMajor && Math.abs(value - mediumValue) < minorStep * 0.05;

    ticks.push({
      value,
      kind: isMajor ? "major" : isMedium ? "medium" : "minor",
      lengthPx: isMajor ? 12 : isMedium ? 8 : 4,
    });
  }

  return ticks;
}

export function formatRulerLabel(value: number): string {
  const rounded = Math.round(value * 1000) / 1000;
  if (Math.abs(rounded) < 1e-9) {
    return "0";
  }

  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(/\.0$/, "");
}
